import { useCallback } from 'react';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import { useEnableCreateOrderFromAsn } from '../../../../../../../feature-flags/hooks/use-enable-create-order-from-asn';
import { useModal } from '../../../../../../../hooks/use-modal';
import { DEFAULT_ORDERS_PAGE_URL } from '../../../../../../../services/user-permission-service';
import Global from '../../../../../../../store/global-selectors';
import { useAdvancedShippingNoticeContext } from '../../advanced-shipping-notice-context';
import { SETUP_CONFIGURATION_STEP, SHIPPING_OPTIONS } from '../constants';
import styles from './almost-done-card.module.less';
import { CreateOrderFromAsnModal } from '../../../create-order-from-asn-modal/create-order-from-asn-modal';
import { ScheduleFreightCard } from '../schedule-freight/schedule-freight-card';

const FreightDropoffTitle = () => {
  const { freightConfiguration } = useAdvancedShippingNoticeContext();
  if (freightConfiguration?.confirmedDropOffDate) {
    return <Text textType={TextType.H4} value={messages.freightDropOffDateConfirmedTitle} />;
  }
  if (freightConfiguration) {
    return <Text textType={TextType.H4} value={messages.freightDropoffSubmissionPendingTitle} />;
  }
  return <Text textType={TextType.H4} value={messages.freightScheduleTitle} />;
};

const FreightDropoffChildren = () => {
  const userTimeFormat = useSelector(Global.selectUserUserTimeFormat);
  const { freightConfiguration } = useAdvancedShippingNoticeContext();
  const { isOpen, open, close } = useModal(false);
  if (freightConfiguration?.confirmedDropOffDate) {
    return (
      <>
        <div className={styles['icon-line']}>
          <FontAwesomeIcon className={styles.icon} icon={faClock} />
          <Text
            formattedMessageValues={{
              carrierName: freightConfiguration.carrierContact.name,
              recievingCenter: freightConfiguration.receivingCenter,
              datetime: DateTime.fromISO(freightConfiguration.confirmedDropOffDate, { zone: 'utc' }).toFormat(
                `MMMM d, yyyy 'at' ${userTimeFormat}`,
              ),
            }}
            textType={TextType.Paragraph}
            value={messages.freightDropOffDateConfirmedText}
          />
        </div>
        <div className={styles['icon-line']}>
          <FontAwesomeIcon className={styles.icon} icon={faCalendar} />
          <Button onClick={open} theme='link'>
            <Text textType={TextType.Paragraph} value={messages.freightDropoffRescheduleText} />
          </Button>
        </div>
        {isOpen ? <ScheduleFreightCard closeModal={close} /> : null}
      </>
    );
  }
  if (freightConfiguration) {
    return <Text textType={TextType.Paragraph} value={messages.freightDropoffSubmissionPendingText} />;
  }
  return (
    <>
      <Text textType={TextType.Paragraph} value={messages.freightScheduleText} />
      <div>
        <Button onClick={open} theme='tertiary'>
          <Text textType={TextType.Paragraph} value={messages.proposeDropOffDatesButtonText} />
        </Button>
      </div>
      {isOpen ? <ScheduleFreightCard closeModal={close} /> : null}
    </>
  );
};

const freightDropoffSubmissionStep = {
  title: <FreightDropoffTitle />,
  children: <FreightDropoffChildren />,
  shouldRender: (savedPayloads) => savedPayloads[SETUP_CONFIGURATION_STEP].packageType !== SHIPPING_OPTIONS.parcel,
};

const DownloadLabelsChildren = () => {
  const { onDownloadBoxLabels } = useAdvancedShippingNoticeContext();
  return (
    <>
      <Text textType={TextType.Paragraph} value={messages.downloadWarehouseLabelsText} />
      <div>
        <Button onClick={onDownloadBoxLabels} theme={THEME_CONSTANTS.secondary}>
          <Text textType={TextType.Paragraph} value={messages.downloadLabelsButtonText} />
        </Button>
      </div>
    </>
  );
};

const ConfigureShipmentStep = () => {
  const { isOpen, open, close } = useModal(false);
  const { isLoadingWarehouses } = useAdvancedShippingNoticeContext();

  const isCreateOrderFromAsnEnabled = useEnableCreateOrderFromAsn();
  const navigate = useNavigate();

  const handleOpenNewOrderModal = useCallback(() => {
    navigate(`${DEFAULT_ORDERS_PAGE_URL}?openNewOrderModal=true`);
  }, [navigate]);

  return (
    <>
      <Text textType={TextType.Paragraph} value={messages.configureShipmentsText} />
      <div>
        <Button
          className={styles['configure-shipments-button']}
          disabled={isCreateOrderFromAsnEnabled ? isLoadingWarehouses : false}
          onClick={isCreateOrderFromAsnEnabled ? open : handleOpenNewOrderModal}
          theme={THEME_CONSTANTS.secondary}
        >
          <Text textType={TextType.Paragraph} value={messages.configureShipmentsButtonText} />
          <FontAwesomeIcon className={styles['arrow-icon']} icon={faArrowRight} />
        </Button>
      </div>
      {isOpen ? <CreateOrderFromAsnModal onClose={close} /> : null}
    </>
  );
};

const downloadLabelsStep = {
  title: <Text textType={TextType.H4} value={messages.downloadWarehouseLabelsTitle} />,
  children: <DownloadLabelsChildren />,
  shouldRender: () => true,
};

const configureShipmentsStep = {
  title: <Text textType={TextType.H4} value={messages.configureShipmentsTitle} />,
  children: <ConfigureShipmentStep />,
  shouldRender: (savedPayloads) => savedPayloads[SETUP_CONFIGURATION_STEP].packageType === SHIPPING_OPTIONS.parcel,
};

export const nextSteps = [downloadLabelsStep, configureShipmentsStep, freightDropoffSubmissionStep];
